import React, { useState } from 'react';

/**
 * Invite / remove collaborators on a plan. Owner-only editing; non-owners see a
 * read-only "who's on this plan" note. Enforces the same 3-person ceiling the
 * server does (owner + 2) in the UI, and surfaces the "invite sent" / error
 * states from invitePlanCollaborator.
 *
 * Presentational + callback-driven so it's easy to test: the container passes
 * `onInvite(email)` / `onRemove(email)` (the wired callables) which resolve on
 * success and reject with a message on failure.
 */
const MAX_PEOPLE = 3; // owner + 2 collaborators

export default function CollaboratorControl({ meta, isOwner, onInvite, onRemove, max = MAX_PEOPLE }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [message, setMessage] = useState('');

  const memberEmails = (meta && meta.memberEmails) || [];
  const ownerEmail = (meta && meta.ownerEmail) || '';
  const atCap = memberEmails.length >= max - 1; // collaborators capped at max-1

  const submit = async () => {
    const value = email.trim();
    if (!value || status === 'sending') return;
    setStatus('sending'); setMessage('');
    try {
      await onInvite(value);
      setStatus('sent');
      setMessage(`Invite sent to ${value.toLowerCase()}.`);
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Could not send the invite. Please try again.');
    }
  };

  const remove = async (target) => {
    try { await onRemove(target); } catch { /* parent surfaces via snapshot */ }
  };

  return (
    <div className="collab-control">
      <div className="collab-people">
        <span className="collab-title">Collaborators</span>
        <ul className="collab-list">
          <li className="collab-owner">{ownerEmail || 'Owner'} <span className="collab-tag">owner</span></li>
          {memberEmails.map((m) => (
            <li key={m}>
              {m}
              {isOwner && (
                <button type="button" className="collab-remove" title="Remove collaborator" onClick={() => remove(m)}>✕</button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {isOwner ? (
        <div className="collab-invite">
          {atCap ? (
            <p className="collab-cap">Maximum of {max} people reached — remove a collaborator to invite someone else.</p>
          ) : (
            <>
              <label className="collab-label">Invite a collaborator by email</label>
              <div className="collab-row">
                <input
                  type="email"
                  className="collab-email"
                  placeholder="co-writer@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (status !== 'idle') { setStatus('idle'); setMessage(''); } }}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                />
                <button type="button" className="btn solid" onClick={submit} disabled={status === 'sending' || !email.trim()}>
                  {status === 'sending' ? 'Sending…' : 'Invite'}
                </button>
              </div>
              <p className="collab-hint">They can edit this plan for free — no licence of their own needed.</p>
            </>
          )}
          {message && <p className={status === 'error' ? 'collab-error' : 'collab-sent'}>{message}</p>}
        </div>
      ) : (
        <p className="collab-note">You're collaborating on this plan. Changes you make are shared with everyone here.</p>
      )}
    </div>
  );
}
